import { Card, HStack, Divider, Box, Text, VStack } from "@chakra-ui/react";
import moment from "moment";
import React from "react";
import SimpleSelect from "@components/ChakraUI/SimpleSelect";
import { getFormatTime, getSwedishWeekDayName, getTimeDiffInHours } from "@utils/dateUtils";
import CheckCircleIcon from "@components/icons/CheckCircleIcon";
import EmptyWhiteCircle from "@components/icons/EmptyWhiteCircle";
import type { ShiftItem } from "@models/Order";
import TooltipInfoIcon from "@components/icons/TooltipInfoIcon";
import { BreakTimeList, ShiftTimeList } from "@constants/createOrder";

interface ShiftItemProps {
	onChange: (dateItem: ShiftItem) => void;
	start: string;
	end: string;
	breakTime?: number;
	isExpanded?: boolean;
	tempStatus?: "BOOKED_WITH_CONSULTANT" | "BOOKED_WITHOUT_CONSULTANT" | "TEMPORARY_ADDED" | "DEFAULT";
}

const ShiftEditItem = ({
	onChange,
	start,
	end,
	breakTime = 0,
	isExpanded = false,
	tempStatus = "DEFAULT",
}: ShiftItemProps) => {
	const shiftItemData = { start, end, break: breakTime ?? 0, isExpanded, tempStatus };

	const isEqualToCurrentDate = moment().isSame(moment(start), "day");
	const startTimeAndEndTimeDiff =
		getFormatTime(start) !== "00:00" && getFormatTime(end) !== "00:00"
			? getTimeDiffInHours(getFormatTime(start), getFormatTime(end))
			: -1;
	const isDisabled = tempStatus === "BOOKED_WITH_CONSULTANT";

	return (
		<Card m={2} borderRadius={"18px"} bg={isExpanded ? "#edeff3" : "#FFFFFF"}>
			{!isExpanded ? (
				<Box w="100%" my={"auto"} p={3}>
					<HStack
						alignItems={"center"}
						cursor={"pointer"}
						onClick={() => {
							isDisabled
								? undefined
								: onChange({ ...shiftItemData, isExpanded: shiftItemData.isExpanded ? false : true });
						}}
					>
						{isDisabled ? <CheckCircleIcon fillColor={"#9EA0A6"} /> : <EmptyWhiteCircle />}
						<Text fontWeight={500} fontSize={14} my={1} color={"#999999"} textTransform={"capitalize"}>
							{`${getSwedishWeekDayName(start)} ${isEqualToCurrentDate ? "(idag)" : ""}`}
						</Text>
					</HStack>
				</Box>
			) : (
				<VStack w="100%" p={3} alignItems={"flex-start"} spacing="3">
					<HStack
						cursor={"pointer"}
						onClick={() => onChange({ ...shiftItemData, isExpanded: shiftItemData.isExpanded ? false : true })}
					>
						<CheckCircleIcon />
						<Text fontWeight={500} fontSize={14} color={"#999999"} textTransform={"capitalize"} m="0">
							{`${getSwedishWeekDayName(start)} ${isEqualToCurrentDate ? "(idag)" : ""}`}
						</Text>
					</HStack>
					<Divider borderColor={"#D7D7D7"} />
					<HStack>
						<SimpleSelect
							label="Starttid"
							width={"40%"}
							onChange={(e) =>
								onChange({
									...shiftItemData,
									start: e.target.value
										? moment(shiftItemData.start)
												.set({
													hour: Number(e.target.value.split(":")[0]),
													minute: Number(e.target.value.split(":")[1]),
												})
												.format()
										: shiftItemData.start,
								})
							}
							value={getFormatTime(start)}
							options={ShiftTimeList}
							placeHolder="Välj"
						/>
						<SimpleSelect
							label="Sluttid"
							width={"40%"}
							onChange={(e) =>
								onChange({
									...shiftItemData,
									end: e.target.value
										? moment(shiftItemData.end)
												.set({
													hour: Number(e.target.value.split(":")[0]),
													minute: Number(e.target.value.split(":")[1]),
												})
												.format()
										: shiftItemData.end,
								})
							}
							value={getFormatTime(end)}
							options={ShiftTimeList}
							placeHolder="Välj"
						/>
					</HStack>
					{startTimeAndEndTimeDiff >= 0 && startTimeAndEndTimeDiff < 4 && (
						<Text fontWeight={500} color={"#EB5757"} fontSize={"12px"} m="0" mt="-1">
							Passet måste vara minst 4 timmar
						</Text>
					)}
					<SimpleSelect
						options={BreakTimeList}
						label="Rast (min)"
						width={"full"}
						onChange={(e) => onChange({ ...shiftItemData, break: Number(e.target.value) })}
						value={breakTime.toString()}
						placeHolder="--"
						tooltipContent="Eftersom passet är under 5 timmar är den föreslagna rasten 30 minuter lång. Konsulten är berättigad till minst 30 minuter men du kan ange en längre rast om du vill."
						tooltipIcon={
							<span>
								<TooltipInfoIcon />
							</span>
						}
						tooltipPlacement="auto-end"
					/>
				</VStack>
			)}
		</Card>
	);
};

export default ShiftEditItem;
